import type { FC } from "react";
import { memo, useContext, useEffect, useState } from "react";

import clsx from "clsx";

import Typography from "@/design-system/Typography";
import Carousel from "@/design-system/carousel";
import TelemetryButton from "@/design-system/telemetry-button";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { getFromUrl, removeFromUrl } from "@Components/Modules/Helpers";
import { cashbackTransactionTab } from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QuerySmMax, colors, genericStyles } from "../../../styles/tokens.stylex";
import { AvailabilityState } from "../CashbackCommonInterfaces";
import { CashbackDashboardContext } from "./CashbackDashboardContext";
import { CashbackMainPageResources } from "./CashbackMainPage.Resources";
import { CashbackMainPageResourcesEn } from "./CashbackMainPage.Resources.En";
import type { IScrollPagination } from "./ScrollPagination";
import ScrollPagination from "./ScrollPagination";
import TextLabel from "./TextLabel";

import * as css from "./TransactionList.module.css";

export interface ITransactionList {
    transPagination: IScrollPagination;
    shouldShowModal: () => void;
    isTemporaryPage?: boolean; // To remove after DPPRD-44182
}

const TransactionList: FC<ITransactionList> = memo(
    (props) => {
        const { totalWithdrawalAmount } = useContext(CashbackDashboardContext);
        const [selectedTab, setSelectedTab] = useState(AvailabilityState.Pending);
        const { isMobileOrTablet } = useViewportInfoContext();
        const mainResources = languageSelector(CashbackMainPageResources, CashbackMainPageResourcesEn);

        useEffect(() => {
            const tabFromUrl = getFromUrl(cashbackTransactionTab);
            if (!tabFromUrl) {
                return;
            }
            const tabNumber = Number(tabFromUrl);
            if (tabNumber >= 0 && tabNumber < 4) {
                setSelectedTab(tabNumber);
                removeFromUrl(cashbackTransactionTab);
            }
        }, []);

        const resources = mainResources.transactionsList;

        const tabsObject: { [key in AvailabilityState]: { eventName: string; text: string } } = {
            [AvailabilityState.Pending]: {
                text: resources.pendingBtn,
                eventName: "cshbck-trxPendingTab",
            },
            [AvailabilityState.Approved]: {
                text: resources.approvedBtn,
                eventName: "cshbck-trxApprovedTab",
            },
            [AvailabilityState.Unknown]: {
                text: resources.withdrawalBtn,
                eventName: "cshbck-trxWithdrawalTab",
            },
            [AvailabilityState.Rejected]: {
                text: resources.rejectedBtn,
                eventName: "cshbck-trxRejectedTab",
            },
        };

        const tabsOrder = [
            AvailabilityState.Pending,
            AvailabilityState.Approved,
            AvailabilityState.Unknown,
            AvailabilityState.Rejected,
        ];

        const getLink = (tab: AvailabilityState, index: number): React.ReactNode => {
            if (!tabsObject[tab]) {
                return null;
            }

            return (
                <TelemetryButton
                    as="button"
                    key={tab}
                    variant="transparent"
                    size="None"
                    styleX={[styles.btn, index === 0 && styles.noPadding]}
                    onClick={() => setSelectedTab(tab)}
                    eventName={tabsObject[tab].eventName}
                >
                    <Typography
                        textElement="h3"
                        fontStyle="BodyM"
                        styleX={[styles.headerStatus, tab === selectedTab && styles.headerActive]}
                    >
                        {tabsObject[tab].text}
                    </Typography>
                </TelemetryButton>
            );
        };

        const tabsCarousel = tabsObject && (
            <Carousel styleX={styles.carouselOverride} showArrows={false}>
                {tabsOrder.map(getLink)}
            </Carousel>
        );

        const availabilityUnknown = selectedTab === AvailabilityState.Unknown;

        const tableHeaders = (
            <div>
                {availabilityUnknown && (
                    <div {...stylex.props(styles.withdrawalInfo)}>
                        <TextLabel
                            id="totalWithdrawalInfo"
                            variant="whiteBig"
                            labelText={mainResources.cashbackDashboard.totalAmountWithdrawalText}
                            text={totalWithdrawalAmount || ""}
                        />
                    </div>
                )}
                <div className={css.tableHeaders}>
                    <div />
                    <div>
                        {availabilityUnknown ? resources.tableHeader1Withdrawal : resources.tableHeader1}
                    </div>
                    <div>{availabilityUnknown ? resources.tableHeader2Mobile : resources.tableHeader2}</div>
                    <div>{!availabilityUnknown && resources.tableHeader3}</div>
                    <div>
                        {availabilityUnknown ? resources.tableHeader4Withdrawal : resources.tableHeader4}
                    </div>
                </div>
            </div>
        );

        const renderEmptyMessage = (availability: AvailabilityState) => {
            let message = null;
            switch (availability) {
                case AvailabilityState.Pending:
                    message = (
                        <>
                            <span>{resources.emptyPendingTransactionsTitle}</span>
                            <span>{resources.emptyPendingTransactionsDescription1}</span>
                            <span>{resources.emptyPendingTransactionsDescription2}</span>
                        </>
                    );
                    break;
                case AvailabilityState.Approved:
                    message = (
                        <>
                            <span>{resources.emptyApprovedTransactionsTitle}</span>
                            <span>{resources.emptyApprovedTransactionsDescription1}</span>
                            <span>{resources.emptyApprovedTransactionsDescription2}</span>
                        </>
                    );
                    break;
                case AvailabilityState.Unknown:
                    message = (
                        <>
                            <span>{resources.emptyWithdrawalsTransactionsTitle}</span>
                            <span>{resources.emptyWithdrawalsTransactionsDescription1}</span>
                            <span>{resources.emptyWithdrawalsTransactionsDescription2}</span>
                        </>
                    );
                    break;

                case AvailabilityState.Rejected:
                    message = (
                        <>
                            <span>{resources.emptyRejectedTransactionsTitle}</span>
                            <span>{resources.emptyRejectedTransactionsDescription}</span>
                        </>
                    );
                    break;
                default:
                    break;
            }

            return (
                <div className={css.transactionsEmptyList}>
                    <div className={css.emptyContainer}>{message}</div>
                </div>
            );
        };

        const claimBtn = (
            <TelemetryButton
                as="button"
                size="M"
                variant={props.isTemporaryPage ? "secondary" : "transparent"}
                styleX={[styles.claimBtn, props.isTemporaryPage && styles.claimBtnCustomPadding]}
                onClick={props.shouldShowModal}
                eventName="cshbck-trxMakeClaim"
            >
                {resources.cashbackLinkTitle}
            </TelemetryButton>
        );

        return (
            <div
                className={clsx(
                    css.transactionListComponent,
                    availabilityUnknown && css.withdrawal,
                    totalWithdrawalAmount && css.hasWithdrawal,
                )}
            >
                <div className={css.header}>
                    {tabsCarousel}
                    {!isMobileOrTablet && claimBtn}
                </div>
                {isMobileOrTablet && claimBtn}
                <ScrollPagination
                    {...props.transPagination}
                    headers={tableHeaders}
                    emptyListElement={renderEmptyMessage(selectedTab)}
                    isMobile={isMobileOrTablet}
                    transactionStatus={selectedTab}
                    isTemporaryPage={props.isTemporaryPage}
                />
            </div>
        );
    },
    (prev, next) => {
        return prev.transPagination !== next.transPagination;
    },
);

const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    carouselOverride: {
        margin: 0,
        gap: { default: "40px", [smMax]: "20px" },
        alignItems: "center",
        gridAutoColumns: "max-content",
        position: "relative",
        width: "auto",
    },

    headerStatus: {
        margin: 0,
        color: colors.grayLight,
        position: "relative",
        paddingBottom: "5px",
        paddingLeft: "8px",
        paddingRight: "8px",
        paddingTop: "10px",
    },

    headerActive: {
        color: colors.b_BrandMagenta,
        borderBottomColor: colors.b_BrandMagenta,
        borderBottomStyle: "solid",
        borderBottomWidth: "2px",
    },

    noPadding: {
        paddingLeft: 0,
    },

    btn: {
        padding: { default: null, [smMax]: 0 },
        color: colors.bg_BrandMagenta,
    },

    claimBtn: {
        alignSelf: "center",
        color: colors.bg_BrandMagenta,
        display: "block",
        fontSize: "0.875rem",
        fontWeight: "bold",
        justifySelf: "end",
        textAlign: "right",
        marginRight: ComposableSpacing.s2, // To remove after DPPRD-44182
        width: "auto",
    },
    claimBtnCustomPadding: {
        paddingBottom: ComposableSpacing.s2,
        paddingLeft: ComposableSpacing.s2,
        paddingRight: ComposableSpacing.s2,
        paddingTop: ComposableSpacing.s2,
    },

    withdrawalInfo: {
        padding: { default: "15px 10px", [smMax]: 10 },
        borderRadius: genericStyles.borderRadiusS1,
        alignItems: "center",
        backgroundImage: "linear-gradient(77deg, #1b2670 0%, #dc2765 100%)",
        display: "flex",
    },
});

export default TransactionList;
